import { useGetChoices } from "@features/ux/components/shared/hooks";
import {
  useCreateWorkflow,
  useGetAllRecipientList,
  useGetAllSegments,
  useGetWorkflowDetails,
  useUpdateWorkflow,
} from "@features/ux/components/workflow/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { PageHeader } from "@shared/components/PageHeader";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { toast } from "@shared/ui/use-toast";
import { FIELD_CLASSES } from "@shared/utils/constants";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface StepHelpers {
  getStepData: (stepIndex: number) => any;
  setStepData: (stepIndex: number, data: any) => void;
}

interface WorkflowInfoStepProps {
  editMode: boolean;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  globalFormData?: any;
  updateGlobalFormData?: (data: any) => void;
}

const generateDaysOfMonthOptions = () => {
  const options = [];
  for (let day = 1; day <= 29; day++) {
    options.push({ label: day.toString(), value: day });
  }
  return options;
};

export const WorkflowInfoStep: React.FC<WorkflowInfoStepProps> = ({
  editMode,
  stepHelpers,
  currentStepIndex = 0,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  globalFormData,
  updateGlobalFormData,
  clearValidationError,
  isValidating,
}) => {
  const { id, action } = useParams();
  const navigate = useNavigate();
  const utilityId = useRemoteUtilityId();
  const dataLoadedRef = useRef(false);

  // API hooks
  const { data: workflowData } = useGetWorkflowDetails(Number(id));
  const { data: getAudienceType = [] } = useGetChoices("audience_type");
  const { data: getActionType = [] } = useGetChoices("action_type");
  const { data: getEventType = [] } = useGetChoices("event_types");
  const { data: segment = [] } = useGetAllSegments();
  const { data: recipientList = [] } = useGetAllRecipientList();

  // API mutation hooks
  const { mutateAsync: createWorkflow, isPending: isCreating } =
    useCreateWorkflow();
  const { mutateAsync: updateWorkflow, isPending: isUpdating } =
    useUpdateWorkflow(id);

  // Memoized options with safe defaults
  const mapChoices = (data: any) =>
    data?.result?.map((item: any) => ({
      value: item.key,
      label: item.value,
    })) || [];

  const audienceTypeOptions = useMemo(
    () => mapChoices(getAudienceType),
    [getAudienceType]
  );

  const actionTypeOptions = useMemo(
    () => mapChoices(getActionType),
    [getActionType]
  );

  const eventTypeOptions = useMemo(
    () => mapChoices(getEventType),
    [getEventType]
  );

  const segmentOptions = useMemo(
    () =>
      segment?.result?.map((choice) => ({
        value: choice.id,
        label: choice.name,
      })) || [],
    [segment]
  );

  const recipientListOptions = useMemo(
    () =>
      recipientList?.results?.map((choice) => ({
        value: choice.id,
        label: `${choice.name} (${choice.totalRecipients || 0} recipients)`,
        total: choice.totalRecipients,
      })) || [],
    [recipientList]
  );

  const scheduleTypeOptions = useMemo(
    () => [
      { value: "schedule_later", label: "Schedule for Later" },
      { value: "recurring", label: "Recurring" },
    ],
    []
  );

  const frequencyOptions = useMemo(() => [{ value: 0, label: "Daily" }], []);

  // showWhen condition function
  const showWhenCondition = (fieldName: string) => {
    return (formValues: any) => {
      const actionType = formValues.actionType;

      switch (actionType) {
        case "0":
          return ["segment", "recipientList"].includes(fieldName);

        case "1":
          if (
            ["segment", "recipientList", "scheduleType"].includes(fieldName)
          ) {
            return true;
          }

          const scheduleType = formValues.scheduleType;
          if (scheduleType === "schedule_later") {
            return ["scheduleDate", "scheduleTime"].includes(fieldName);
          }
          if (scheduleType === "recurring") {
            return ["frequency", "startDate", "endDate", "time"].includes(
              fieldName
            );
          }
          const frequency = formValues.frequency;
          if (frequency === 2) {
            return ["day_of_month"].includes(fieldName);
          }
          return false;

        case "2":
          return fieldName === "triggerEvent";

        default:
          return false;
      }
    };
  };

  // Form fields
  const formFields: FormField[] = [
    // Workflow Configuration Section
    {
      name: "workflowName",
      type: "text",
      label: "Workflow Name",
      placeholder: 'e.g., "Monthly Bill Reminder"',
      showHelperTooltip: true,
      tooltipText:
        "Choose a clear, descriptive name to easily identify this workflow (e.g., 'Monthly Bill Reminder').",
      required: true,
      defaultValue: data.workflowName || "",
      className: "col-span-2",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder:
        "Add details about the workflow's purpose, goals, or any special instructions (optional)",
      showHelperTooltip: true,
      tooltipText:
        "Add details about the workflow's purpose, goals, or any special instructions (optional).",
      required: false,
      rows: 3,
      defaultValue: data.description || "",
      className: "col-span-2",
    },
    {
      name: "audienceType",
      type: "select",
      label: "Audience Type",
      placeholder: "Select audience type",
      showHelperTooltip: true,
      tooltipText:
        "Select the type of recipients this workflow will target. This filters available lists and triggers.",
      required: true,
      options: audienceTypeOptions,
      defaultValue: data.audienceType || "",
    },
    {
      name: "actionType",
      type: "select",
      label: "Action Type",
      placeholder: "Select action type",
      showHelperTooltip: true,
      tooltipText: "Workflow triggers at specific times or intervals.",
      required: true,
      options: actionTypeOptions,
      defaultValue: data.actionType || "",
    },

    // Conditional Fields for Immediate & Time-Based
    {
      name: "segment",
      type: "select",
      label: "Segment",
      placeholder: "Select segment",
      showHelperTooltip: true,
      tooltipText:
        "Select a segment to filter recipients within the selected list.",
      required: false,
      options: segmentOptions,
      defaultValue: data.segment || "",
      showWhen: showWhenCondition("segment"),
    },
    {
      name: "recipientList",
      type: "select",
      label: "Recipient List",
      placeholder: "Select recipient list",
      showHelperTooltip: true,
      tooltipText:
        "Choose the recipient list that will receive communications from this workflow.",
      required: false,
      options: recipientListOptions,
      defaultValue: data.recipientList || "",
      showWhen: showWhenCondition("recipientList"),
    },

    // Time-Based Specific Fields
    {
      name: "scheduleType",
      type: "select",
      label: "Schedule Type",
      placeholder: "Select schedule type",
      showHelperTooltip: true,
      tooltipText:
        "Messages will be sent as soon as the workflow is activated.",
      required: false,
      options: scheduleTypeOptions,
      defaultValue: data.scheduleType || "",
      showWhen: showWhenCondition("scheduleType"),
      className: "col-span-2",
    },

    // Schedule for Later Fields
    {
      name: "day_of_month",
      label: "Day of the Month",
      type: "select",
      required: false,
      options: generateDaysOfMonthOptions(),
      placeholder: "Select day of month...",
      fullWidth: true,
      defaultValue: "",
      helperText: "Select which day of the month to run the cycle (1-29)",
      showWhen: showWhenCondition("day_of_month"),
      className: "col-span-1 mt-3",
    },
    {
      name: "scheduleDate",
      type: "datepicker",
      label: "Schedule Date",
      placeholder: "Select date",
      showHelperTooltip: true,
      tooltipText: "Select the date when the workflow should be executed.",
      required: false,
      defaultValue: data.scheduleDate || "",
      showWhen: showWhenCondition("scheduleDate"),
    },
    {
      name: "scheduleTime",
      type: "time",
      label: "Schedule Time",
      placeholder: "Select time",
      showHelperTooltip: true,
      tooltipText: "Select the time when the workflow should be executed.",
      required: false,
      defaultValue: data.scheduleTime || "",
      showWhen: showWhenCondition("scheduleTime"),
    },

    // Recurring Fields
    {
      name: "frequency",
      type: "select",
      label: "Frequency",
      placeholder: "Select frequency",
      showHelperTooltip: true,
      tooltipText: "Choose how often this workflow should repeat.",
      required: false,
      options: frequencyOptions,
      defaultValue: data.frequency || "",
      showWhen: showWhenCondition("frequency"),
    },
    {
      name: "startDate",
      type: "datepicker",
      label: "Start Date",
      placeholder: "Select start date",
      showHelperTooltip: true,
      tooltipText: "Select when the recurring workflow should begin.",
      required: false,
      defaultValue: data.startDate || "",
      showWhen: showWhenCondition("startDate"),
    },
    {
      name: "endDate",
      type: "datepicker",
      label: "End Date",
      placeholder: "Select end date",
      showHelperTooltip: true,
      tooltipText: "Select when the recurring workflow should end (optional).",
      required: false,
      defaultValue: data.endDate || "",
      showWhen: showWhenCondition("endDate"),
    },
    {
      name: "time",
      type: "time",
      label: "Time",
      placeholder: "Select time",
      showHelperTooltip: true,
      tooltipText:
        "Select the time when the recurring workflow should execute.",
      required: false,
      defaultValue: data.time || "",
      showWhen: showWhenCondition("time"),
    },
    {
      name: "triggerEvent",
      type: "select",
      label: "Trigger Event",
      placeholder: "Select trigger event",
      showHelperTooltip: true,
      tooltipText: "Select the event that will trigger this workflow.",
      required: false,
      options: eventTypeOptions,
      defaultValue: data.triggerEvent || "",
      showWhen: showWhenCondition("triggerEvent"),
      className: "col-span-2",
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: data,
  });

  // Data initialization effect
  useEffect(() => {
    if (dataLoadedRef.current) return;

    switch (action) {
      case "edit":
        if (workflowData) {
          const result = workflowData.result;
          const eventTypeKey =  JSON.stringify(result.eventType)
          console.log("eventTypeKey", eventTypeKey)
          const editData = {
            workflowName: result.name || "",
            description: result.description || "",
            audienceType: result.audienceType?.toString() || "",
            actionType: result.actionType?.toString() || "",
            segment: result.segment?.toString() || "",
            recipientList: result.recipientList?.toString() || "",
            scheduleType: result.scheduleType || "",
            scheduleDate: result.scheduleDate || "",
            scheduleTime: result.scheduleTime || "",
            frequency: result.frequency?.toString() || "",
            startDate: result.startDate || "",
            endDate: result.endDate || "",
            time: result.scheduleTime || "",
            triggerEvent: eventTypeKey || "",
            day_of_month: result.dayOfMonth?.toString() || "",
          };

          // Filter out empty strings to prevent padStart error on date/time fields
          const cleanedEditData = Object.fromEntries(
            Object.entries(editData).filter(([_, value]) => value !== "")
          );

          onDataChange?.(editData);
          FormService.populateForm(form, cleanedEditData, formFields);
          dataLoadedRef.current = true;
        }
        break;

      default:
        // For create mode
        if (stepHelpers) {
          const savedData = stepHelpers.getStepData(currentStepIndex) || {};
          if (Object.keys(savedData).length > 0) {
            onDataChange?.(savedData);
          }
        }
        dataLoadedRef.current = true;
        break;
    }
  }, [
    action,
    workflowData,
    stepHelpers,
    currentStepIndex,
    onDataChange,
    form,
    formFields,
  ]);

  const handleFieldChange = (fieldName, value) => {
    onDataChange?.({ ...data, [fieldName]: value });
  };
  const buildPayload = (formData) => {
    const actionType = formData.actionType;
    const scheduleType = formData.scheduleType;

    const basePayload: Record<string, any> = {
      remote_utility_id: utilityId,
      name: formData.workflowName,
      description: formData.description,
      audience_type: Number(formData.audienceType),
      action_type: Number(actionType),
    };

    // Helper function to get recipient list total
    const getRecipientTotal = (recipientListId: string) => {
      const selectedList = recipientListOptions.find(
        (option) => option.value === Number(recipientListId)
      );
      return selectedList?.total || 0;
    };

    switch (actionType) {
      case "0": // Immediate
        basePayload.segment = formData.segment
          ? Number(formData.segment)
          : null;

        if (formData.recipientList) {
          basePayload.recipient_list = Number(formData.recipientList);
          basePayload.recipients = getRecipientTotal(formData.recipientList);
        }
        break;

      case "1": // Time-Based
        basePayload.segment = formData.segment
          ? Number(formData.segment)
          : null;

        if (formData.recipientList) {
          basePayload.recipient_list = Number(formData.recipientList);
          basePayload.recipients = getRecipientTotal(formData.recipientList);
        }

        basePayload.schedule_type = formData.scheduleType;

        switch (scheduleType) {
          case "schedule_later":
            basePayload.schedule_date = formData.scheduleDate;
            basePayload.schedule_time = formData.scheduleTime;
            break;

          case "recurring":
            basePayload.frequency = Number(formData.frequency);
            basePayload.start_date = formData.startDate;
            basePayload.end_date = formData.endDate;
            basePayload.schedule_time = formData.time;

            // Add day_of_month for monthly frequency
            if (formData.frequency === 2 && formData.day_of_month) {
              basePayload.day_of_month = Number(formData.day_of_month);
            }
            break;
        }
        break;

      case "2": // Event-Based
        basePayload.event_type = formData.triggerEvent;
        break;

      case "3":
        basePayload.frequency = Number(formData.frequency);
        basePayload.start_date = formData.startDate;
        basePayload.end_date = formData.endDate;
        basePayload.time = formData.time;
        break;

      case "4":
        basePayload.event_type = formData.triggerEvent;
        break;

      default:
        break;
    }

    const cleanedPayload = Object.fromEntries(
      Object.entries(basePayload).filter(
        ([, value]) => value !== "" && value !== null && value !== undefined
      )
    );

    return cleanedPayload;
  };
  const handleNext = async () => {
    const formData = form.getValues();

    try {
      const payload = buildPayload(formData);
      let response;
      let workflowDraftId;

      switch (action) {
        case "edit":
          response = await updateWorkflow(payload);
          workflowDraftId = stepHelpers?.getStepData(0)?.id || id;
          break;

        case "create":
          response = await createWorkflow(payload);
          workflowDraftId = response?.result?.id;
          break;
      }

      if (workflowDraftId) {
        const completedData = {
          ...formData,
          payload,
          workflowDraftId,
          response,
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        };

        onDataChange?.(completedData);
        onNext?.();
        navigate(
          `/communication-hub/workflows/edit/${workflowDraftId}#setup-actions`
        );
      } else {
        setValidationError?.(
          "Failed to create workflow draft. Please try again."
        );
      }
    } catch (error) {
      setValidationError?.("Failed to submit. Please try again.");
    }
  };

  return (
    <div className="bg-white p-3">
      <div className="mb-2">
        <PageHeader
          title="Workflow Configuration"
          subtitle="Define the basic workflow settings"
        />
      </div>

      <DynamicForm
        fields={formFields}
        form={form}
        onFieldChange={handleFieldChange}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
        config={{
          layout: "grid",
          columns: 2,
          showSubmitButton: false,
          showResetButton: false,
          spacing: "medium",
          classes: FIELD_CLASSES,
        }}
      />

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button onClick={onPrevious} disabled={isFirstStep} variant="outline">
          Previous
        </Button>
        <Button
          onClick={handleNext}
          disabled={isValidating || isCreating || isUpdating}
        >
          {isCreating || isUpdating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
              {isCreating ? "Creating..." : "Updating..."}
            </>
          ) : isValidating ? (
            "Validating..."
          ) : (
            "Continue to Next Step"
          )}
        </Button>
      </div>
    </div>
  );
};
